import { getElementBounds } from '../../lib/bound';
import { isLayerBelow, sortElementsByLayer } from '../../lib/element';
import { generateElementCanvas } from '../../lib/render';
import { useEditorRuntimeStore } from '../../stores/useEditorRuntimeStore';
import { useEditorStore } from '../../stores/useEditorStore';

interface CacheEntry {
  canvas: HTMLCanvasElement;
  renderZoom: number;
  offsetX: number;
  offsetY: number;
  timestamp: number;
}

const retouchCanvasCache = new Map<string, CacheEntry>();
const MAX_CACHE_SIZE = 100;

const getCacheKey = (
  targetLayer: number,
  zoom: number,
  elementIds: string[],
  baseImageId: string,
): string => {
  return `${targetLayer}-${zoom}-${baseImageId}-${elementIds.join(',')}`;
};

const limitCacheSize = () => {
  if (retouchCanvasCache.size > MAX_CACHE_SIZE) {
    const sortedEntries = Array.from(retouchCanvasCache.entries()).sort(
      (a, b) => a[1].timestamp - b[1].timestamp,
    );
    const entriesToDelete = sortedEntries.slice(
      0,
      retouchCanvasCache.size - MAX_CACHE_SIZE,
    );
    for (const [key] of entriesToDelete) {
      retouchCanvasCache.delete(key);
    }
  }
};

export const clearRetouchCanvasCache = () => {
  retouchCanvasCache.clear();
};

export const renderRetouchCanvas = (
  targetLayer: number,
  zoom: number,
): {
  canvas: HTMLCanvasElement;
  renderZoom: number;
  offsetX: number;
  offsetY: number;
} | null => {
  const { elements } = useEditorStore.getState();
  const { plugins } = useEditorRuntimeStore.getState();

  const baseImage = Array.from(elements.values()).find(
    (element) => element.type === 'image' && element.isVisible,
  );

  if (!baseImage) {
    return null;
  }

  const retouchElements = Array.from(elements.values())
    .filter((element) => {
      const plugin = plugins.get(element.type);
      return (
        plugin?.render.isRetouchTool && isLayerBelow(element.layer, targetLayer)
      );
    })
    .toSorted(sortElementsByLayer);

  const renderZoom = Math.min(zoom, 1.0);
  const elementIds = retouchElements.map((el) => el.id);
  const cacheKey = getCacheKey(
    targetLayer,
    renderZoom,
    elementIds,
    baseImage.id,
  );

  const cachedEntry = retouchCanvasCache.get(cacheKey);
  if (cachedEntry) {
    cachedEntry.timestamp = Date.now();
    return {
      canvas: cachedEntry.canvas,
      renderZoom: cachedEntry.renderZoom,
      offsetX: cachedEntry.offsetX,
      offsetY: cachedEntry.offsetY,
    };
  }

  const canvasWidth = Math.ceil(baseImage.width * renderZoom);
  const canvasHeight = Math.ceil(baseImage.height * renderZoom);

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const context = canvas.getContext('2d');

  if (!context) return null;

  context.clearRect(0, 0, canvasWidth, canvasHeight);

  context.save();
  context.scale(renderZoom, renderZoom);

  const elementsToRender = [baseImage, ...retouchElements];

  for (const element of elementsToRender) {
    const elementCanvas = generateElementCanvas(element, {
      zoom: renderZoom,
      offsetX: -baseImage.x,
      offsetY: -baseImage.y,
    });

    if (!elementCanvas) continue;

    const [x1, y1] = getElementBounds(element, { withRotation: false });

    const canvasX = x1 - baseImage.x - elementCanvas.padding;
    const canvasY = y1 - baseImage.y - elementCanvas.padding;

    context.drawImage(
      elementCanvas.canvas,
      canvasX,
      canvasY,
      elementCanvas.canvas.width / elementCanvas.scale,
      elementCanvas.canvas.height / elementCanvas.scale,
    );
  }

  context.restore();

  const result = {
    canvas,
    renderZoom,
    offsetX: baseImage.x,
    offsetY: baseImage.y,
  };

  retouchCanvasCache.set(cacheKey, {
    ...result,
    timestamp: Date.now(),
  });

  limitCacheSize();

  return result;
};
